import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowRight, Gift, Trophy, Award, Calendar, Code, Rocket,
    ChevronDown, Bot, Sparkles, Star, MessageSquare, Cpu, Globe,
    Brain, Lock, Zap
} from 'lucide-react';

// Enhanced gradient layer with more complex animations
const GradientLayer = ({ message = "Loading...",
    variant = "default",
    duration = 7,
    width = 20,
    rounded = 'none' }) => {
    const colorSchemes = {
        default: {
            colors: [
                { h: 0, s: 100, l: 50 },   // c1: Bright Christmas red
                { h: 120, s: 70, l: 80 },  // c2: Rich evergreen green
                { h: 50, s: 80, l: 60 },   // c3: Festive gold
                { h: 0, s: 0, l: 95 }      // c4: Snowy white
            ],
            neutral: { h: 0, s: 0, l: 90 } // Neutral: Light snowy white
        },
        lavenderDusk: {
            colors: [
                { h: 200, s: 20, l: 95 }, // c1: Icy white-blue
                { h: 270, s: 30, l: 85 }, // c2: Pale lavender
                { h: 280, s: 40, l: 50 }, // c3: Rich purple
                { h: 290, s: 45, l: 35 }  // c4: Deep violet
            ],
            neutral: { h: 285, s: 20, l: 60 }
        },
        mintForest: {
            colors: [
                { h: 140, s: 30, l: 85 }, // c1: Pale mint
                { h: 150, s: 45, l: 65 }, // c2: Bright leaf green
                { h: 160, s: 40, l: 40 }, // c3: Deep forest
                { h: 130, s: 30, l: 55 }  // c4: Mossy green
            ],
            neutral: { h: 155, s: 15, l: 50 }
        },
        desertGlow: {
            colors: [
                { h: 50, s: 30, l: 90 }, // c1: Pale sandy highlight
                { h: 35, s: 45, l: 70 }, // c2: Warm dune
                { h: 20, s: 50, l: 50 }, // c3: Sandstone
                { h: 10, s: 45, l: 35 }  // c4: Terracotta
            ],
            neutral: { h: 25, s: 20, l: 50 }
        },
        roseGarden: {
            colors: [
                { h: 200, s: 20, l: 95 }, // c1: Icy white-blue
                { h: 330, s: 50, l: 70 }, // c2: Vibrant rose
                { h: 320, s: 55, l: 45 }, // c3: Magenta rose
                { h: 300, s: 40, l: 30 }  // c4: Deep plum
            ],
            neutral: { h: 320, s: 20, l: 55 }
        },
        sunsetCoral: {
            colors: [
                { h: 200, s: 20, l: 95 }, // c1: Icy white-blue
                { h: 20, s: 55, l: 65 }, // c2: Vibrant coral
                { h: 10, s: 60, l: 40 }, // c3: Burnt orange-red
                { h: 0, s: 50, l: 25 }   // c4: Deep earthy red
            ],
            neutral: { h: 15, s: 20, l: 50 }
        },
        arcticAurora: {
            colors: [
                { h: 195, s: 25, l: 95 }, // c1: Icy white-blue highlight
                { h: 180, s: 40, l: 80 }, // c2: Pastel aqua
                { h: 170, s: 50, l: 50 }, // c3: Teal
                { h: 210, s: 55, l: 30 }  // c4: Deep polar navy
            ],
            neutral: { h: 190, s: 20, l: 60 }
        }
    };

    // Ensure variant is valid, fallback to default if not
    const selectedVariant = colorSchemes[variant] ? variant : 'default';
    const { colors: colorArray, neutral } = colorSchemes[selectedVariant];

    const colorStrings = useMemo(() => {
        const [c1, c2, c3, c4] = colorArray;
        return {
            c1: `hsla(${c1.h}, ${c1.s}%, ${c1.l}%, 0.5)`,
            c2: `hsla(${c2.h}, ${c2.s}%, ${c2.l}%, 0.5)`,
            c3: `hsla(${c3.h}, ${c3.s}%, ${c3.l}%, 0.5)`,
            c4: `hsla(${c4.h}, ${c4.s}%, ${c4.l}%, 0.5)`,
            neutral: `hsla(${neutral.h}, ${neutral.s}%, ${neutral.l}%, 0.2)`
        };
    }, [colorArray, neutral]);

    const createMotionVariant = (index) => {
        const baseDuration = duration * (1 + index * 0.3);
        const direction = index % 2 === 0 ? 1 : -1;
        return {
            animate: {
                rotate: [0, 360 * direction],
                scale: [1, 1.05, 1],
                x: [0, 10 * direction, 0, -10 * direction, 0],
                y: [0, -10 * direction, 0, 10 * direction, 0],
            },
            transition: {
                rotate: {
                    duration: baseDuration,
                    repeat: Infinity,
                    ease: "linear"
                },
                scale: {
                    duration: baseDuration * 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                },
                x: {
                    duration: baseDuration * 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                },
                y: {
                    duration: baseDuration * 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                }
            }
        };
    };

    const layers = [
        { color: 'c1', radialX: 50, radialY: 50 },
        { color: 'c2', radialX: 30, radialY: 70 },
        { color: 'c3', radialX: 70, radialY: 30 },
        { color: 'c4', radialX: 20, radialY: 20 },
        { color: 'neutral', radialX: 80, radialY: 80 },
        { color: 'c2', radialX: 60, radialY: 60 },
        { color: 'c3', radialX: 40, radialY: 40 }
    ];

    return (
        <div className="h-full w-full inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center z-50">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`w-full h-full aspect-square`}
            >
                <div className="relative w-full h-full">
                    <div className={`absolute inset-0 rounded-${rounded} shadow-2xl border border-[0.5px]  overflow-hidden`}>
                        {layers.map((layer, index) => (
                            <motion.div
                                key={index}
                                className="absolute inset-[-50%]"
                                {...createMotionVariant(index)}
                            >
                                <div
                                    className="w-full h-full opacity-90"
                                    style={{
                                        background: `radial-gradient(
                          circle at ${layer.radialX}% ${layer.radialY}%,
                          ${colorStrings[layer.color]} 0%,
                          transparent 45%
                        )`
                                    }}
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>


        </div>
    );
};

const FloatingPhone = () => {
    return (
        <motion.div
            initial={{ y: 0 }}
            animate={{ y: [-1, 10, -1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-full"
        >
            <div className="w-32 h-64 bg-black rounded-3xl relative overflow-hidden border-4 border-gray-800 mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-red-400/20" />
                <motion.div
                    animate={{
                        y: [-200, 400],
                        opacity: [1, 1, 1]
                    }}
                    transition={{
                        duration: 50,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute inset-x-0 h-32 bg-gradient-to-b from-green-500/20 to-red-500/20 blur-xl"
                />
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-700 rounded-full" />
                <motion.div
                    animate={{
                        scale: [1, 1, 1],
                        rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                >
                    <Bot className="w-12 h-12 text-white/80" />
                </motion.div>
            </div>

            <motion.div
                animate={{
                    rotate: 360,
                    scale: [1, 1.2, 1]
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                }}
                className="absolute -top-4 -right-4"
            >
                <Sparkles className="w-6 h-6 text-yellow-400" />
            </motion.div>

            <motion.div
                animate={{
                    rotate: -360,
                    scale: [1, 0.8, 1]
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                }}
                className="absolute -bottom-4 -left-4"
            >
                <Star className="w-5 h-5 text-purple-400" />
            </motion.div>
        </motion.div>
    );
};

const AnnouncementCard = ({ icon: Icon, title, date, description }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl p-6 flex flex-col gap-2 shadow-sm hover:shadow-lg transition-shadow"
    >
        <div className="flex items-center gap-3">
            <div className="p-2 bg-black text-white rounded-lg">
                <Icon className="w-5 h-5" />
            </div>
            <div>
                <h3 className="font-semibold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-600">{date}</p>
            </div>
        </div>
        <p className="text-gray-700 text-sm mt-2">{description}</p>
    </motion.div>
);

const PrizeCard = ({ place, prizeDetail, icon: Icon, extras }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        whileHover={{ y: -5 }}
        className="bg-white/80 backdrop-blur-md border border-gray-200 rounded-xl p-6 shadow-sm"
    >
        <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl">
                <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
                <h3 className="text-xl font-bold text-gray-900">{place}</h3>
                <p className="text-lg font-medium text-gray-800">{prizeDetail}</p>
            </div>
        </div>
        <ul className="space-y-2">
            {extras.map((item, index) => (
                <li key={index} className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-gray-700">{item}</span>
                </li>
            ))}
        </ul>
    </motion.div>
);

const Home = () => {
    const [isHovered, setIsHovered] = useState(false);

    // Enhanced gradient layers with more pastel colors
    const gradientLayers = [
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(140, 50%, 85%, 0.6)', radialX: 20, radialY: 20, delay: 0.6 }, // Soft pastel green
        { color: 'hsla(40, 80%, 70%, 0.7)', radialX: 50, radialY: 50, delay: 0.2 },  // Pastel gold
        { color: 'hsla(0, 70%, 75%, 0.6)', radialX: 60, radialY: 60, delay: 1 },    // Pastel red
        { color: 'hsla(140, 40%, 80%, 0.5)', radialX: 45, radialY: 45, delay: 0.8 }, // Soft green
        { color: 'hsla(200, 20%, 95%, 0.8)', radialX: 50, radialY: 50, delay: 0 }, // Icy white
        { color: 'hsla(40, 60%, 65%, 0.6)', radialX: 45, radialY: 45, delay: 1.4 }, // Soft gold
        { color: 'hsla(0, 50%, 70%, 0.7)', radialX: 35, radialY: 65, delay: 1.8 },  // Light pastel red
        { color: 'hsla(140, 45%, 70%, 0.5)', radialX: 35, radialY: 65, delay: 2 }   // Muted pastel green
    ];




    const announcements = [
        {
            title: "Registration Opens",
            date: "December 1, 2024",
            description: "Early bird registration begins with special perks",
            icon: Calendar
        },
        {
            title: "Kickoff Event",
            date: "December 20, 2024",
            description: "Virtual opening ceremony and team formation",
            icon: Rocket
        },
        {
            title: "Development Phase",
            date: "December 20-24, 2024",
            description: "Build your innovative AI solutions",
            icon: Code
        },
        {
            title: "Final Submissions",
            date: "December 24, 2024",
            description: "Project submissions and documentation due",
            icon: Gift
        },
        {
            title: "Demo Day",
            date: "December 25, 2024",
            description: "Project presentations and winner announcement",
            icon: Trophy
        }
    ];

    const prizes = [
        {
            place: "First Place",
            prizeDetail: "500 BLAND credits, $500 cash",
            icon: Trophy
        },
        {
            place: "Second Place",
            prizeDetail: "250 BLAND credits, $250 cash",
            icon: Award
        },
        {
            place: "Third Place",
            prizeDetail: "250 BLAND credits, $250 cash",
            icon: Award
        }
    ];


    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            {/* Hero Section with Centered Gradient Circle */}
            <div className="min-h-screen flex flex-col items-center justify-center relative py-10 px-2">
                {/* Centered Gradient Circle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] md:h-[700px] rounded-none shadow-2xl overflow-hidden">
                    <div className="absolute inset-0">
                        {gradientLayers.map((layer, index) => (
                            <GradientLayer
                                key={index}
                                {...layer}
                                direction={index % 2 === 0 ? 1 : -1}
                            />
                        ))}
                    </div>
                </div>

                {/* Hero Content */}
                <div className="relative z-10">
                    <motion.div
                        initial={{ opacity: 1, y: 2 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-6 max-w-xl flex flex-col"
                    >
                        <div className="inline-flex items-center space-x-2 px-2 py-1 bg-black text-white rounded-full mb-3 mx-auto">
                            <Sparkles className="w-3 h-3" />
                            <span className="text-xs font-medium">December 20-25, 2024</span>
                        </div>

                        <h1 className="text-4xl font-bold mb-3 text-gray-900">
                            A <span className="italic"> Bland</span>  Holiday Hackathon
                        </h1>
                        <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                            Join us in revolutionizing voice AI technology. Build the next generation
                            of conversational experiences and compete for exciting prizes.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 1, scale: 1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 50, delay: 0.2 }}
                        className="mb-6 mx-auto w-full"
                    >
                        <FloatingPhone className="" />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 2 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="flex flex-col sm:flex-row justify-center gap-2"
                        onClick={() => { window.open("https://forms.gle/JUFwYE3J1RkqSMwP8") }}
                    >
                        <motion.button
                            onHoverStart={() => setIsHovered(true)}
                            onHoverEnd={() => setIsHovered(false)}
                            className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg font-medium text-sm"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Register Now
                            <motion.span
                                animate={{ x: isHovered ? 5 : 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <ArrowRight className="ml-1 h-4 w-4" />
                            </motion.span>
                        </motion.button>
                    </motion.div>
                </div>
            </div>


            {/* Prizes Section */}
            <div className="py-28 bg-gradient-to-b from-white to-gray-50 relative">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="h-64 w-64 bg-yellow-400/20 blur-3xl rounded-full absolute -top-10 -left-10"></div>
                    <div className="h-64 w-64 bg-red-400/20 blur-3xl rounded-full absolute top-1/2 right-10 transform -translate-y-1/2"></div>
                </div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    {/* Animated Heading */}
                    <motion.h2
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="text-5xl font-extrabold text-center tracking-wide mb-6 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-red-500"
                    >
                        ✨ Prizes Fit for Champions ✨
                    </motion.h2>

                    {/* Subtext */}
                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                        viewport={{ once: true }}
                        className="text-lg text-center text-gray-700 leading-relaxed max-w-3xl mx-auto mb-14"
                    >
                        Create. Innovate. Win big. Beyond the cash, earn glory, recognition, and rewards that last a lifetime.
                    </motion.p>

                    {/* Grid Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {prizes.map((prize, index) => (
                            <motion.div
                                key={index}
                                initial={{ scale: 0.9, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.7, delay: index * 0.2 }}
                                viewport={{ once: true }}
                                className="p-8 bg-white shadow-lg rounded-lg text-center transform hover:scale-105 transition-all duration-300"
                            >
                                <div className="flex justify-center mb-6">
                                    <prize.icon className="h-16 w-16 text-yellow-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                    {prize.place}
                                </h3>
                                <p className="text-lg text-gray-600 font-medium mb-4">
                                    {prize.prizeDetail}
                                </p>
                                <div className="h-1 w-20 mx-auto bg-gradient-to-r from-yellow-400 to-red-500 rounded-full"></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>


            {/* Features Section */}
            <div className="py-20 bg-white/50 backdrop-blur-sm">
                <div className="max-w-6xl mx-auto px-4">
                    <motion.h2
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-4xl font-bold text-center mb-4"
                    >
                        Build with Advanced AI
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-gray-600 text-center mb-12 max-w-2xl mx-auto"
                    >
                        Access cutting-edge voice AI technology and comprehensive development tools
                    </motion.p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: MessageSquare,
                                title: "Natural Conversations",
                                description: "Create AI agents that understand context and maintain natural dialogue flow"
                            },
                            {
                                icon: Globe,
                                title: "Multi-language Support",
                                description: "Build solutions that work seamlessly across different languages and accents"
                            },
                            {
                                icon: Cpu,
                                title: "Real-time Processing",
                                description: "Leverage our high-performance infrastructure for instant responses"
                            },
                            {
                                icon: Brain,
                                title: "Advanced AI Models",
                                description: "Access state-of-the-art language models and voice synthesis"
                            },
                            {
                                icon: Lock,
                                title: "Enterprise Security",
                                description: "Bank-grade security features built into every interaction"
                            },
                            {
                                icon: Zap,
                                title: "Quick Integration",
                                description: "Simple API integration with comprehensive documentation"
                            }
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="p-2 bg-black rounded-lg">
                                        <feature.icon className="h-6 w-6 text-white" />
                                    </div>
                                    <h3 className="font-semibold text-lg">{feature.title}</h3>
                                </div>
                                <p className="text-gray-600">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="py-20">
                <div className="max-w-4xl mx-auto px-4">
                    <motion.h2
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-4xl font-bold text-center mb-12"
                    >
                        Frequently Asked Questions
                    </motion.h2>
                    <div className="space-y-6">
                        {[
                            {
                                q: "Who can participate?",
                                a: "The hackathon is open to all developers, designers, and creators passionate about AI and voice technology."
                            },
                            {
                                q: "What kind of projects can I build?",
                                a: "Any project that utilizes Bland AI's voice API creatively. Think customer service, education, entertainment, or accessibility solutions."
                            },

                            {
                                q: "What support will be provided?",
                                a: "You'll have access to technical documentation, mentorship sessions, and our developer support team throughout the event."
                            }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-gray-200"
                            >
                                <h3 className="font-semibold text-lg mb-2">{item.q}</h3>
                                <p className="text-gray-600">{item.a}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-20 bg-black text-white">
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to build the future?</h2>
                    <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                        Join us this December for an unforgettable hackathon experience. Transform how AI communicates with the world.
                    </p>
                    <motion.button
                        className="inline-flex items-center px-8 py-4 bg-white text-black rounded-xl font-medium"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => { window.open("https://forms.gle/JUFwYE3J1RkqSMwP8") }}
                    >
                        Register Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </motion.button>
                </div>
            </footer>
        </div>

    );
};

export default Home;